/**
 * Globals API Module
 * Handles all global configuration API operations
 */

import { api } from '../core/api.client';
import { API_ENDPOINTS } from '../core/api.config';
import {
  ApiClientResponse,
  RequestOptions,
  GlobalSettings,
} from '../core/api.types';
import { PolicyGlobal } from '../../types/policyType';
import { ContactGlobal } from '../../types/contactType';

/**
 * Generic Global API class
 */
export class GlobalApi<T = any> {
  constructor(private globalSlug: string) {}

  /**
   * Get global data
   */
  async get(options?: RequestOptions): Promise<ApiClientResponse<T>> {
    return api.get<T>(`globals/${this.globalSlug}`, options);
  }

  /**
   * Update global data
   */
  async update(data: Partial<T>, options?: RequestOptions): Promise<ApiClientResponse<T>> {
    return api.post<T>(`globals/${this.globalSlug}`, data, options);
  }

  /**
   * Get specific field from global
   */
  async getField<K extends keyof T>(field: K, options?: RequestOptions): Promise<ApiClientResponse<T[K]>> {
    const response = await this.get(options);
    
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data[field],
      };
    }

    return {
      success: false,
      error: response.error,
    };
  }

  /**
   * Update specific field in global
   */
  async updateField<K extends keyof T>(
    field: K,
    value: T[K],
    options?: RequestOptions
  ): Promise<ApiClientResponse<T>> {
    const updateData = {} as Partial<T>;
    updateData[field] = value;
    return this.update(updateData, options);
  }
}

/**
 * Site Settings Global API
 */
export class SiteSettingsApi extends GlobalApi<GlobalSettings> {
  constructor() {
    super(API_ENDPOINTS.GLOBALS.SITE_SETTINGS);
  }

  /**
   * Get site logo
   */
  async getLogo(options?: RequestOptions): Promise<ApiClientResponse<string | null>> {
    const response = await this.getField('logo', options);
    
    if (response.success) {
      // Handle both Media object and string URL
      const logo = response.data;
      if (typeof logo === 'string') {
        return { success: true, data: logo };
      } else if (logo && typeof logo === 'object' && 'url' in logo) {
        return { success: true, data: logo.url };
      }
    }

    return { success: true, data: null };
  }

  /**
   * Get site SEO settings
   */
  async getSEO(options?: RequestOptions): Promise<ApiClientResponse<GlobalSettings['seo']>> {
    return this.getField('seo', options);
  }

  /**
   * Get social media links
   */
  async getSocialMedia(options?: RequestOptions): Promise<ApiClientResponse<GlobalSettings['socialMedia']>> {
    return this.getField('socialMedia', options);
  }

  /**
   * Update site logo
   */
  async updateLogo(logoUrl: string, options?: RequestOptions): Promise<ApiClientResponse<GlobalSettings>> {
    return this.updateField('logo', logoUrl, options);
  }

  /**
   * Update SEO settings
   */
  async updateSEO(seo: GlobalSettings['seo'], options?: RequestOptions): Promise<ApiClientResponse<GlobalSettings>> {
    return this.updateField('seo', seo, options);
  }

  /**
   * Update social media links
   */
  async updateSocialMedia(
    socialMedia: GlobalSettings['socialMedia'],
    options?: RequestOptions
  ): Promise<ApiClientResponse<GlobalSettings>> {
    return this.updateField('socialMedia', socialMedia, options);
  }
}

/**
 * Navigation Global API
 */
export interface NavigationItem {
  id: string;
  label: string;
  url: string;
  icon?: string;
  target?: '_self' | '_blank';
  children?: NavigationItem[];
  order?: number;
  isActive?: boolean;
}

export interface NavigationGlobal {
  mainMenu?: NavigationItem[];
  footerMenu?: NavigationItem[];
  mobileMenu?: NavigationItem[];
}

export class NavigationApi extends GlobalApi<NavigationGlobal> {
  constructor() {
    super(API_ENDPOINTS.GLOBALS.NAVIGATION);
  }

  /**
   * Get main navigation menu
   */
  async getMainMenu(options?: RequestOptions): Promise<ApiClientResponse<NavigationItem[]>> {
    const response = await this.getField('mainMenu', options);
    return {
      ...response,
      data: response.data || [],
    };
  }

  /**
   * Get footer navigation menu
   */
  async getFooterMenu(options?: RequestOptions): Promise<ApiClientResponse<NavigationItem[]>> {
    const response = await this.getField('footerMenu', options);
    return {
      ...response,
      data: response.data || [],
    };
  }

  /**
   * Get mobile navigation menu
   */
  async getMobileMenu(options?: RequestOptions): Promise<ApiClientResponse<NavigationItem[]>> {
    const response = await this.getField('mobileMenu', options);
    return {
      ...response,
      data: response.data || [],
    };
  }

  /**
   * Update main navigation menu
   */
  async updateMainMenu(
    menu: NavigationItem[],
    options?: RequestOptions
  ): Promise<ApiClientResponse<NavigationGlobal>> {
    return this.updateField('mainMenu', menu, options);
  }

  /**
   * Update footer navigation menu
   */
  async updateFooterMenu(
    menu: NavigationItem[],
    options?: RequestOptions
  ): Promise<ApiClientResponse<NavigationGlobal>> {
    return this.updateField('footerMenu', menu, options);
  }

  /**
   * Update mobile navigation menu
   */
  async updateMobileMenu(
    menu: NavigationItem[],
    options?: RequestOptions
  ): Promise<ApiClientResponse<NavigationGlobal>> {
    return this.updateField('mobileMenu', menu, options);
  }
}

/**
 * Footer Global API
 */
export interface FooterGlobal {
  copyright?: string;
  description?: string;
  links?: Array<{
    label: string;
    url: string;
    target?: '_self' | '_blank';
  }>;
  address?: string;
  phone?: string;
  email?: string;
}

export class FooterApi extends GlobalApi<FooterGlobal> {
  constructor() {
    super(API_ENDPOINTS.GLOBALS.FOOTER);
  }

  /**
   * Get copyright text
   */
  async getCopyright(options?: RequestOptions): Promise<ApiClientResponse<string>> {
    const response = await this.getField('copyright', options);
    return {
      ...response,
      data: response.data || `© ${new Date().getFullYear()} KTX Group`,
    };
  }

  /**
   * Get contact information
   */
  async getContactInfo(options?: RequestOptions): Promise<ApiClientResponse<{
    address?: string;
    phone?: string;
    email?: string;
  }>> {
    const response = await this.get(options);
    
    if (response.success && response.data) {
      return {
        success: true,
        data: {
          address: response.data.address,
          phone: response.data.phone,
          email: response.data.email,
        },
      };
    }

    return {
      success: false,
      error: response.error,
    };
  }

  /**
   * Update copyright text
   */
  async updateCopyright(copyright: string, options?: RequestOptions): Promise<ApiClientResponse<FooterGlobal>> {
    return this.updateField('copyright', copyright, options);
  }

  /**
   * Update contact information
   */
  async updateContactInfo(
    contactInfo: {
      address?: string;
      phone?: string;
      email?: string;
    },
    options?: RequestOptions
  ): Promise<ApiClientResponse<FooterGlobal>> {
    return this.update(contactInfo, options);
  }
}

/**
 * Policy Global API
 */
export class PolicyGlobalApi extends GlobalApi<PolicyGlobal> {
  constructor() {
    super(API_ENDPOINTS.GLOBALS.LANDING_POLICY);
  }

  /**
   * Get policy data with cache-busting timestamp
   */
  async get(options?: RequestOptions): Promise<ApiClientResponse<PolicyGlobal>> {
    const timestampOptions = {
      ...options,
      params: {
        ...options?.params,
        t: Date.now().toString()
      }
    };
    return super.get(timestampOptions);
  }

  /**
   * Get policy content with cache-busting
   */
  async getContent(options?: RequestOptions): Promise<ApiClientResponse<string | null>> {
    const timestampOptions = {
      ...options,
      params: {
        ...options?.params,
        t: Date.now().toString()
      }
    };
    const response = await this.getField('content', timestampOptions);
    return {
      ...response,
      data: response.data || null,
    };
  }

  /**
   * Update policy content
   */
  async updateContent(content: string, options?: RequestOptions): Promise<ApiClientResponse<PolicyGlobal>> {
    return this.updateField('content', content, options);
  }
}

/**
 * Contact Global API
 */
export class ContactGlobalApi extends GlobalApi<ContactGlobal> {
  constructor() {
    super(API_ENDPOINTS.GLOBALS.LANDING_CONTACT);
  }

  /**
   * Get contact data with cache-busting timestamp
   */
  async get(options?: RequestOptions): Promise<ApiClientResponse<ContactGlobal>> {
    const timestampOptions = {
      ...options,
      params: {
        ...options?.params,
        t: Date.now().toString()
      }
    };
    return super.get(timestampOptions);
  }

  /**
   * Get contact information formatted
   */
  async getContactInfo(options?: RequestOptions): Promise<ApiClientResponse<{
    address: string;
    website: string;
    phone: string;
    email: string;
  } | null>> {
    const response = await this.get(options);
    
    if (response.success && response.data) {
      return {
        success: true,
        data: {
          address: response.data.address,
          website: response.data.website,
          phone: response.data.phone,
          email: response.data.email,
        },
      };
    }

    return {
      success: false,
      error: response.error,
    };
  }

  /**
   * Update contact information
   */
  async updateContactInfo(
    contactInfo: {
      address?: string;
      website?: string;
      phone?: string;
      email?: string;
    },
    options?: RequestOptions
  ): Promise<ApiClientResponse<ContactGlobal>> {
    return this.update(contactInfo, options);
  }
}

/**
 * Create custom global API instance
 */
export function createGlobalApi<T = any>(globalSlug: string): GlobalApi<T> {
  return new GlobalApi<T>(globalSlug);
}

// Export pre-configured instances
export const siteSettingsApi = new SiteSettingsApi();
export const navigationApi = new NavigationApi();
export const footerApi = new FooterApi();
export const policyGlobalApi = new PolicyGlobalApi();
export const contactGlobalApi = new ContactGlobalApi();

// Export all globals APIs
export const globals = {
  siteSettings: siteSettingsApi,
  navigation: navigationApi,
  footer: footerApi,
  policy: policyGlobalApi,
  contact: contactGlobalApi,
};
